import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpenIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ContentAnalysis {
  wordCount: number;
  readingTime: number;
  headingCount: number;
  headingStructure: string[];
  contentDepthScore: number;
  recommendations: string[];
}

const ContentDepthOptimizer = () => {
  const [analysis, setAnalysis] = useState<ContentAnalysis | null>(null);

  useEffect(() => {
    const analyzePageContent = () => {
      const content = document.body.innerText || '';
      const words = content.trim().split(/\s+/).filter(word => word.length > 0);
      const wordCount = words.length;
      const readingTime = Math.ceil(wordCount / 200); // Average reading speed

      // Analyze heading structure
      const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
      const headingStructure = headings.map(h => h.tagName.toLowerCase());
      const headingCount = headings.length;

      // Calculate content depth score
      let contentDepthScore = 0;
      
      // Word count scoring (0-40 points)
      if (wordCount >= 2000) contentDepthScore += 40;
      else if (wordCount >= 1500) contentDepthScore += 30;
      else if (wordCount >= 1000) contentDepthScore += 20;
      else if (wordCount >= 500) contentDepthScore += 10;

      // Heading structure scoring (0-30 points)
      if (headingCount >= 8) contentDepthScore += 30;
      else if (headingCount >= 6) contentDepthScore += 20;
      else if (headingCount >= 4) contentDepthScore += 15;
      else if (headingCount >= 2) contentDepthScore += 10;

      // Reading time scoring (0-20 points)
      if (readingTime >= 8) contentDepthScore += 20;
      else if (readingTime >= 5) contentDepthScore += 15;
      else if (readingTime >= 3) contentDepthScore += 10;

      // Semantic structure scoring (0-10 points)
      const hasArticleTag = document.querySelector('article') !== null;
      const hasSectionTags = document.querySelectorAll('section').length >= 3;
      if (hasArticleTag && hasSectionTags) contentDepthScore += 10;
      else if (hasArticleTag || hasSectionTags) contentDepthScore += 5;

      // Generate recommendations
      const recommendations: string[] = [];
      
      if (wordCount < 1000) {
        recommendations.push('Consider expanding content to at least 1000 words for better SEO');
      }
      
      if (headingCount < 4) {
        recommendations.push('Add more headings (H2, H3) to improve content structure');
      }
      
      if (readingTime < 3) {
        recommendations.push('Add more comprehensive information to increase reading time');
      }

      if (!hasArticleTag) {
        recommendations.push('Consider wrapping main content in <article> tag for better semantics');
      }

      if (!hasSectionTags) {
        recommendations.push('Use <section> tags to organize content into logical sections');
      }

      setAnalysis({
        wordCount,
        readingTime,
        headingCount,
        headingStructure,
        contentDepthScore,
        recommendations
      });
    };

    // Analyze content after a short delay to ensure page is fully loaded
    const timer = setTimeout(analyzePageContent, 1000);
    return () => clearTimeout(timer);
  }, []);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 80) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Poor';
  };

  if (!analysis) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpenIcon className="h-5 w-5" />
            Content Depth Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Analyzing page content...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpenIcon className="h-5 w-5" />
          Content Depth Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Overall Score */}
          <div className="text-center">
            <div className={`text-3xl font-bold ${getScoreColor(analysis.contentDepthScore)}`}>
              {analysis.contentDepthScore}/100
            </div>
            <Badge variant={analysis.contentDepthScore >= 80 ? 'default' : 'secondary'}>
              {getScoreBadge(analysis.contentDepthScore)}
            </Badge>
          </div>

          {/* Content Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-foreground">{analysis.wordCount}</div>
              <div className="text-sm text-muted-foreground">Words</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-foreground">{analysis.readingTime}</div>
              <div className="text-sm text-muted-foreground">Min Read</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-foreground">{analysis.headingCount}</div>
              <div className="text-sm text-muted-foreground">Headings</div>
            </div>
          </div>

          {/* Heading Structure */}
          <div>
            <h4 className="font-semibold mb-2">Heading Structure</h4>
            <div className="flex flex-wrap gap-1">
              {analysis.headingStructure.map((heading, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {heading}
                </Badge>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {analysis.recommendations.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4" />
                Recommendations
              </h4>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Success Indicators */}
          {analysis.contentDepthScore >= 80 && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircleIcon className="h-4 w-4" />
              <span>Great content depth! This page provides comprehensive information.</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ContentDepthOptimizer;